import { useMemo } from "react";
import { useTranslation } from "react-i18next";

/**
 * Pure derived-data transforms for the dashboard, extracted verbatim from
 * UnifiedStockerDashboard.jsx (see Technical_Docs/Architecture_Rebuild/
 * 49_FULL_STACK_REBUILD_IMPLEMENTATION_PLAN.md, Phase 2). No behavior
 * change -- every memo body, every "BUG FIX" comment documenting a prior
 * fix, and every dependency array is preserved exactly. This hook reads
 * already-fetched Redux state and local UI state; it dispatches nothing.
 */
export function useDashboardTransforms({
  watchlistItems,
  batchSnapshots,
  sentimentAggState,
  watchlistSimpleCharts,
  contextRadarState,
  posts,
  groups,
  groupSearchResults,
  filings,
  expandedStock,
  credState,
  userId,
  followersList,
}) {
  const { t } = useTranslation();

  // DATA TRANSFORMATION — WATCHLIST
  // BUG FIX: field was `changePercent` but memos referenced `stock.change`.
  // Now we expose both `changePercent` AND `change` for backwards compat.
  const watchlistData = useMemo(() => {
    if (!watchlistItems?.length) return [];

    return watchlistItems.map((watchlistItem) => {
      const ticker = watchlistItem.symbol || watchlistItem.ticker;

      const snapshot = batchSnapshots?.find(
        (s) => s.ticker === ticker || s.symbol === ticker
      );

      const lastPrice =
        snapshot?.last_price ??
        snapshot?.c ??
        snapshot?.close ??
        watchlistItem.stock?.lastPrice ??
        null;

      const prevClose =
        snapshot?.prev_close ??
        snapshot?.pc ??
        snapshot?.previousClose ??
        watchlistItem.stock?.prev_close ??
        null;

      let changePercent = null;
      let priceChange = null;

      if (lastPrice !== null && prevClose !== null && prevClose !== 0) {
        priceChange = lastPrice - prevClose;
        changePercent = (priceChange / prevClose) * 100;
      }

      const sentAgg = sentimentAggState.byTicker?.[ticker]?.data;
      const simpleChart = watchlistSimpleCharts[ticker]; // BUG FIX: correct key lookup

      const tickerEvents =
        contextRadarState?.byTicker?.[ticker]?.data ??
        contextRadarState?.watchlist?.data?.filter((e) => e.ticker === ticker) ??
        [];

      const hasNews = tickerEvents.some((e) => e.event_type === "news");
      const hasFilings = tickerEvents.some((e) => e.event_type === "filing");

      // Sparkline data extraction -- fetchWatchlistSimpleChartsThunk can
      // return several shapes depending on the backend and Redux reducer
      // implementation. Try every known shape in priority order, fall back
      // to passing the raw value through (Sparkline has its own
      // normaliseSparklineData() as a final safety net).
      //   { prices: number[] }              <- most common backend shape
      //   { data: { prices: number[] } }    <- one extra nesting level
      //   { data: number[] }                <- flat nested
      //   { data: { data: number[] } }      <- matches StockChart triple-nesting
      //   { results: number[] }             <- Polygon-style aggregates
      //   number[]                          <- reducer stored raw array
      const extractSparklinePrices = (raw) => {
        if (!raw) return [];
        if (Array.isArray(raw)) return raw;
        if (Array.isArray(raw.prices)) return raw.prices;
        if (Array.isArray(raw.data?.prices)) return raw.data.prices;
        if (Array.isArray(raw.data?.data)) return raw.data.data;
        if (Array.isArray(raw.data)) return raw.data;
        if (Array.isArray(raw.results)) return raw.results;
        return raw;
      };

      return {
        id: watchlistItem.id,
        ticker,
        symbol: ticker,
        name: watchlistItem.stock?.name || ticker,

        lastPrice,
        currentPrice: lastPrice,
        prev_close: prevClose,
        changePercent,
        priceChange,
        change: changePercent, // BUG FIX: `change` alias so legacy references don't break

        marketSnapshot: snapshot,
        snapshot,
        volume: snapshot?.v ?? snapshot?.volume ?? 0,

        sentimentAgg: sentAgg || null,

        sparklineData: extractSparklinePrices(simpleChart),

        hasNews,
        hasFilings,
        recentEvents: tickerEvents,
      };
    });
  }, [watchlistItems, batchSnapshots, sentimentAggState, watchlistSimpleCharts, contextRadarState]);

  // DATA TRANSFORMATION — TRENDING CONVERSATIONS
  const trendingConversations = useMemo(() => {
    if (!posts?.length) return [];

    const symbolMentions = {};
    posts.forEach((post) => {
      (post.stocks || []).forEach(({ symbol }) => {
        if (!symbol) return;
        if (!symbolMentions[symbol]) {
          symbolMentions[symbol] = { count: 0, totalCredibility: 0 };
        }
        symbolMentions[symbol].count += 1;
        symbolMentions[symbol].totalCredibility += post.author_credibility?.score || 0;
      });
    });

    return Object.entries(symbolMentions)
      .map(([symbol, info]) => {
        const snapshot = batchSnapshots.find((s) => s.ticker === symbol);
        const agg = sentimentAggState.byTicker?.[symbol]?.data?.all;
        const tone = agg?.combined_score;

        return {
          ticker: symbol,
          name: snapshot?.name || symbol,
          price: snapshot?.last_price || 0,
          change: snapshot?.change_percent || 0,
          mentions: info.count,
          credScore: Math.round(info.count ? info.totalCredibility / info.count : 0),
          sentimentLabel:
            tone > 0
              ? t("dashboard.unifiedDashboard.trending.positiveDiscussion")
              : tone < 0
              ? t("dashboard.unifiedDashboard.trending.negativeDiscussion")
              : t("dashboard.unifiedDashboard.trending.mixedDiscussion"),
          volume: snapshot?.volume ? `${(snapshot.volume / 1_000_000).toFixed(1)}M` : "0",
        };
      })
      .sort((a, b) => b.mentions - a.mentions)
      .slice(0, 4);
  }, [posts, batchSnapshots, sentimentAggState, t]);

  // DATA TRANSFORMATION — CREDIBILITY FEED
  const credibilityFeed = useMemo(() => {
    if (!posts?.length) return [];
    const watchlistSymbols = new Set(watchlistData.map((s) => s.ticker));

    return posts
      .filter((post) => (post.stocks || []).some((s) => watchlistSymbols.has(s.symbol)))
      .map((post) => ({
        id: post.id,
        user: {
          username: post.username || post.user?.username || t("dashboard.unifiedDashboard.feed.anonymous"),
          credibility_score: post.author_credibility?.score || 0,
          avatar_color: "from-blue-500 to-purple-600",
        },
        tickers: (post.stocks || []).map((s) => s.symbol),
        content: post.content || "",
        created_at: post.created_at
          ? new Date(post.created_at).toLocaleString()
          : t("dashboard.unifiedDashboard.feed.recently"),
        comments: post.comments?.length || 0,
        reactions: (post.likes || 0) + (post.bull_count || 0) + (post.bear_count || 0),
      }))
      .slice(0, 10);
  }, [posts, watchlistData, t]);

  // DATA TRANSFORMATION — RELEVANT GROUPS
  const relevantGroups = useMemo(() => {
    const source = groupSearchResults?.length ? groupSearchResults : groups;
    return (source || []).slice(0, 4).map((g) => ({
      id: g.id,
      name: g.name || g.group_name,
      tickers: g.related_tickers || [],
      posts: g.post_count || 0,
      members: g.member_count || 0,
      activity:
        (g.member_count || 0) > 100
          ? t("dashboard.unifiedDashboard.groupActivity.veryActive")
          : t("dashboard.unifiedDashboard.groupActivity.active"),
      joined: !!g.is_member,
    }));
  }, [groups, groupSearchResults, t]);

  // DATA TRANSFORMATION — CONTEXT RADAR (synthetic fallback)
  // BUG FIX: stock.change was undefined before. Now reads the correct field.
  const syntheticContextRadar = useMemo(() => {
    const events = [];

    (filings || []).slice(0, 3).forEach((filing) => {
      events.push({
        type: "filing",
        ticker: filing.ticker ?? expandedStock ?? "N/A",
        title: t("dashboard.unifiedDashboard.syntheticEvents.filingPublished", { form: filing.form }),
        description:
          filing.summary || filing.filing_type || t("dashboard.unifiedDashboard.syntheticEvents.secFilingAvailable"),
        time: filing.filing_date
          ? new Date(filing.filing_date).toLocaleDateString()
          : t("dashboard.unifiedDashboard.recent"),
        importance: ["10-K", "10-Q", "8-K"].includes(filing.form) ? "high" : "medium",
      });
    });

    watchlistData.forEach((stock) => {
      if (Math.abs(stock.change ?? 0) > 3) {
        events.push({
          type: "momentum",
          ticker: stock.ticker,
          title:
            (stock.change ?? 0) > 0
              ? t("dashboard.unifiedDashboard.syntheticEvents.positiveMomentum")
              : t("dashboard.unifiedDashboard.syntheticEvents.negativeMomentum"),
          description:
            (stock.change ?? 0) > 0
              ? t("dashboard.unifiedDashboard.syntheticEvents.priceUpObserved", {
                  percent: Math.abs(stock.change ?? 0).toFixed(1),
                })
              : t("dashboard.unifiedDashboard.syntheticEvents.priceDownObserved", {
                  percent: Math.abs(stock.change ?? 0).toFixed(1),
                }),
          time: t("dashboard.unifiedDashboard.recent"),
          importance: Math.abs(stock.change ?? 0) > 5 ? "high" : "medium",
        });
      }

      const agg = sentimentAggState.byTicker?.[stock.ticker]?.data?.all;
      if (agg) {
        events.push({
          type: "sentiment",
          ticker: stock.ticker,
          title: t("dashboard.unifiedDashboard.syntheticEvents.communitySentimentActivity"),
          description: t("dashboard.unifiedDashboard.syntheticEvents.credibleRetailNoise", {
            credible: agg.credible?.votes ?? 0,
            retail: agg.retail?.votes ?? 0,
            noise: agg.noise?.votes ?? 0,
          }),
          time: t("dashboard.unifiedDashboard.recent"),
          importance: "medium",
        });
      }
    });

    const weight = { high: 3, medium: 2, low: 1 };
    return events
      .sort((a, b) => (weight[b.importance] || 1) - (weight[a.importance] || 1))
      .slice(0, 6);
  }, [watchlistData, filings, expandedStock, sentimentAggState, t]);

  const filteredContextRadar = useMemo(() => {
    if (expandedStock) {
      const byTicker = contextRadarState.byTicker?.[expandedStock]?.data;
      if (Array.isArray(byTicker) && byTicker.length) return byTicker;

      const wl = contextRadarState.watchlist?.data;
      if (Array.isArray(wl) && wl.length) {
        const filtered = wl.filter((e) => e.ticker === expandedStock);
        if (filtered.length) return filtered;
      }

      const gl = contextRadarState.global?.data;
      if (Array.isArray(gl) && gl.length) {
        const filtered = gl.filter((e) => e.ticker === expandedStock);
        if (filtered.length) return filtered;
      }

      return syntheticContextRadar.filter((e) => e.ticker === expandedStock);
    }

    const wl = contextRadarState.watchlist?.data;
    if (Array.isArray(wl) && wl.length) return wl;

    const gl = contextRadarState.global?.data;
    if (Array.isArray(gl) && gl.length) return gl;

    return syntheticContextRadar;
  }, [expandedStock, contextRadarState, syntheticContextRadar]);

  // DATA TRANSFORMATION — TECHNICAL CONTEXT
  // BUG FIX: stock.change was always undefined. Now reads correct alias field.
  const technicalContext = useMemo(() => {
    return watchlistData.slice(0, 3).map((stock) => {
      const agg = sentimentAggState.byTicker?.[stock.ticker]?.data?.all;
      const tone = agg?.combined_score;
      const chg = stock.change ?? 0;

      return {
        ticker: stock.ticker,
        description: t("dashboard.unifiedDashboard.techContext.priceDescription", {
          direction: chg > 0
            ? t("dashboard.unifiedDashboard.techContext.up")
            : t("dashboard.unifiedDashboard.techContext.down"),
          percent: Math.abs(chg).toFixed(1),
          tone:
            tone > 0
              ? t("dashboard.unifiedDashboard.techContext.slightlyPositive")
              : tone < 0
              ? t("dashboard.unifiedDashboard.techContext.slightlyNegative")
              : t("dashboard.unifiedDashboard.techContext.neutral"),
        }),
        strength:
          chg > 3
            ? t("dashboard.unifiedDashboard.techContext.veryStrong")
            : chg > 1
            ? t("dashboard.unifiedDashboard.techContext.strong")
            : t("dashboard.unifiedDashboard.techContext.neutralStrength"),
      };
    });
  }, [watchlistData, sentimentAggState, t]);

  const filteredTechnicalContext = expandedStock
    ? technicalContext.filter((i) => i.ticker === expandedStock)
    : technicalContext;

  // USER ENGAGEMENT
  const userEngagement = useMemo(
    () => ({
      credibility_score: credState.byUser?.[userId]?.data?.global_score || 0,
      posts_7d: posts.filter((p) => p.user_id === userId).length,
      groups_active: (groups || []).filter((g) => g.is_member).length || 0,
      watchlist_size: watchlistItems.length,
      followers: followersList.length || 0,
    }),
    [credState, userId, posts, watchlistItems.length, groups, followersList]
  );

  return {
    watchlistData,
    trendingConversations,
    credibilityFeed,
    relevantGroups,
    syntheticContextRadar,
    filteredContextRadar,
    technicalContext,
    filteredTechnicalContext,
    userEngagement,
  };
}
